import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shad/ui/table'
import {
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  RowSelectionState,
  useReactTable,
} from '@tanstack/react-table'
import { InfoIcon } from 'lucide-react'
import { ApprovalRule } from '../../schema'

interface RuleTableProps {
  columns: ColumnDef<ApprovalRule>[]
  data: ApprovalRule[]
  onRowSelectionChange: (selectedRows: ApprovalRule[]) => void
  selectedRows: number[]
}

export function RuleTable({
  columns,
  data,
  onRowSelectionChange,
  selectedRows,
}: RuleTableProps) {
  // Convert selected row IDs to RowSelectionState format
  const rowSelectionState: RowSelectionState = selectedRows.reduce(
    (acc, rowId) => {
      const rowIndex = data.findIndex((item) => item.id === rowId)
      if (rowIndex !== -1) {
        acc[rowIndex.toString()] = true
      }
      return acc
    },
    {} as Record<string, boolean>
  )

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),

    enableMultiRowSelection: true,
    onRowSelectionChange: (updater) => {
      const newSelection =
        typeof updater === 'function'
          ? updater(table.getState().rowSelection)
          : updater
      console.log(newSelection)
      const selectedData = data.filter(
        (_, index) => newSelection[index.toString()]
      )
      onRowSelectionChange(selectedData)
    },
    state: {
      rowSelection: rowSelectionState,
    },
  })

  return (
    <div className="rounded-md border">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                )
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && 'selected'}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                <div className="flex flex-col items-center gap-2 py-12">
                  <InfoIcon className="size-10 text-gray-800" />
                  <p className="text-gray-600 font-medium">No Results</p>
                </div>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  )
}
